import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteFeaturedIngredientMutation,
  useGetFeaturedIngredientQuery
} from '../../../../../services/endpoints/content/featuredIngredient';
import { FeaturedIngredient } from '../../../../../types/content.type';
import { getErrorMessage } from '../../../../../utils/utils';
import SafeHTMLDisplay from '../../../../common/SafeHTMLDisplay';

export const FeaturedIngredientDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetFeaturedIngredientQuery(+params.id!);
  const [deleteFeaturedIngredient, { isLoading: isDeleting }] =
    useDeleteFeaturedIngredientMutation();
  const [featuredIngredient, setFeaturedIngredient] = useState<FeaturedIngredient>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading featured ingredient', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setFeaturedIngredient(data.featuredIngredient);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteFeaturedIngredient(+params.id!).unwrap();
      addToast('Featured ingredient deleted successfully', 'success');
      navigate('/admin/content/featured-ingredients');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !featuredIngredient) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/content/featured-ingredients')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            FeaturedIngredients List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Featured Ingredient #${featuredIngredient?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() =>
              navigate(`/admin/content/featuredIngredients/${featuredIngredient?.id}/edit`)
            }
          >
            Edit Featured Ingredient
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Featured Ingredient
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Id:"
              text={featuredIngredient?.id}
            />
            <LabelText
              label="Label:"
              text={featuredIngredient?.name}
            />
            <LabelText label="content:">
              <SafeHTMLDisplay htmlContent={featuredIngredient?.content} />
            </LabelText>
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showModal}
        title="Delete Featured Ingredient Plan"
        message="Are you sure you want to remove this Featured Ingredient?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
